/**
 * Vite の import.meta.env から設定値を解決する。
 * 必須値が欠けているときはビルドを止めるため明示的に throw する。
 */

const DEFAULT_API_BASE_URL = "/api/v1";
const DEFAULT_LONG_GENERATION_NOTICE_MS = 8000;

export type ClientEnv = {
  readonly apiBaseUrl: string;
  readonly demoScenarioId: string;
  readonly longGenerationNoticeMs: number;
  readonly debug: boolean;
  readonly maxScenes: number;
  /** ストリーミング経路を使用するかどうか（要件 4.1, 4.5, 4.6）。デフォルト true。 */
  readonly streamingEnabled: boolean;
  /**
   * worldline-palimpsest (Phase 3-1) 2D view 起動ボタンを UI に出すかどうか。
   * "false" のときのみ無効化 (ボタン非表示)、未設定はデフォルト true。
   */
  readonly worldlinePalimpsestEnabled: boolean;
  /**
   * interactive-deduction (Phase 3-3) DeductionPanel を UI に出すかどうか。
   * "false" のときのみ無効化 (Scene.contradictions が non-empty でも Panel を出さない)、
   * 未設定はデフォルト true。backend の INTERACTIVE_DEDUCTION_ENABLED と対になる client フラグ。
   */
  readonly interactiveDeductionEnabled: boolean;
  /**
   * choice-emotion (Phase 4) 拡張: 各選択肢に主人公の内心 1 行 subtitle を表示するかどうか。
   * "false" のときのみ無効化 (choice.emotion が来ても subtitle を出さず、既存 DOM と
   * bit-by-bit 互換)、未設定はデフォルト true。
   */
  readonly choiceEmotionEnabled: boolean;
  /**
   * error-ux (Phase 4) ユーザー向けエラー文言正規化を有効にするか。
   * true (default) で mapProblemDetail が kind ごとのテンプレート body を優先し、
   * server の detail は UIError.serverDetail に保持する。"false" で旧挙動 (detail で
   * body 上書き) に戻り、既存 DOM / 文言と bit-by-bit 互換になる。
   */
  readonly friendlyErrorEnabled: boolean;
  /**
   * scenario-start-ux (Phase 4) ScenarioIntroOverlay を有効化するか。
   * "false" のときのみ無効化 (従来の LoadingOverlay に delegate して bit-by-bit 互換)、
   * 未設定はデフォルト true。
   */
  readonly scenarioStartUxEnabled: boolean;
  /**
   * paragraph-reveal (Phase 4) 本文の段落 (segment 単位) 逐次表示を両 view で有効化するか。
   * "false" のときのみ無効化 (両 view で全 segment 即時表示、本 spec 導入前と bit-by-bit 互換)、
   * 未設定はデフォルト true。motion-reduce が true の場合は flag 値に関わらず即時表示にフォールバック。
   */
  readonly paragraphRevealEnabled: boolean;
};

function readString(key: string): string | undefined {
  const value = import.meta.env[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function readNumber(key: string, fallback: number): number {
  const raw = readString(key);
  if (raw === undefined) return fallback;
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed) || parsed < 0) {
    throw new Error(`Invalid ${key}: expected non-negative integer, got "${raw}"`);
  }
  return parsed;
}

export function loadClientEnv(): ClientEnv {
  const demoScenarioId = readString("VITE_DEMO_SCENARIO_ID");
  if (demoScenarioId === undefined) {
    throw new Error(
      "VITE_DEMO_SCENARIO_ID is required. Set it in .env (see .env.example).",
    );
  }

  // VITE_STREAMING_ENABLED が "false" の場合のみ無効化する。未設定はデフォルト true。
  const streamingEnabledRaw = readString("VITE_STREAMING_ENABLED");
  const streamingEnabled = streamingEnabledRaw !== "false";

  // VITE_WORLDLINE_PALIMPSEST_ENABLED も同じパターン: "false" のみ無効化、デフォルト true。
  const palimpsestRaw = readString("VITE_WORLDLINE_PALIMPSEST_ENABLED");
  const worldlinePalimpsestEnabled = palimpsestRaw !== "false";

  // VITE_INTERACTIVE_DEDUCTION_ENABLED: "false" のみ無効化、デフォルト true。
  const deductionRaw = readString("VITE_INTERACTIVE_DEDUCTION_ENABLED");
  const interactiveDeductionEnabled = deductionRaw !== "false";

  // VITE_CHOICE_EMOTION_ENABLED: "false" のみ無効化、デフォルト true。
  const choiceEmotionRaw = readString("VITE_CHOICE_EMOTION_ENABLED");
  const choiceEmotionEnabled = choiceEmotionRaw !== "false";

  // VITE_FRIENDLY_ERROR_ENABLED: "false" のみ無効化、デフォルト true。
  const friendlyErrorRaw = readString("VITE_FRIENDLY_ERROR_ENABLED");
  const friendlyErrorEnabled = friendlyErrorRaw !== "false";

  // VITE_SCENARIO_START_UX_ENABLED: "false" のみ無効化、デフォルト true。
  const scenarioStartUxRaw = readString("VITE_SCENARIO_START_UX_ENABLED");
  const scenarioStartUxEnabled = scenarioStartUxRaw !== "false";

  // VITE_PARAGRAPH_REVEAL_ENABLED: "false" のみ無効化、デフォルト true。
  const paragraphRevealRaw = readString("VITE_PARAGRAPH_REVEAL_ENABLED");
  const paragraphRevealEnabled = paragraphRevealRaw !== "false";

  return {
    apiBaseUrl: readString("VITE_API_BASE_URL") ?? DEFAULT_API_BASE_URL,
    demoScenarioId,
    longGenerationNoticeMs: readNumber(
      "VITE_LONG_GENERATION_NOTICE_MS",
      DEFAULT_LONG_GENERATION_NOTICE_MS,
    ),
    debug: readString("VITE_DEBUG") === "true",
    maxScenes: readNumber("VITE_MAX_SCENES", 5),
    streamingEnabled,
    worldlinePalimpsestEnabled,
    interactiveDeductionEnabled,
    choiceEmotionEnabled,
    friendlyErrorEnabled,
    scenarioStartUxEnabled,
    paragraphRevealEnabled,
  };
}
